import { NavLink, useNavigate } from 'react-router';
import {
  ChevronDown, Plus, ChevronLeft, ChevronRight, Lock, LayoutGrid,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavItems, type NavItem } from '@/hooks/useNavItems';
import AddLocationModal from './AddLocationModal';
import type { Restaurant, VenueRole } from '@gatheredhq/types';

/** A venue plus the role the signed-in person holds AT THAT venue. */
type VenueWithRole = Restaurant & { _role?: VenueRole | null };

/**
 * Venues you own read as the default and carry no badge — labelling them would be
 * noise on the common case. Anything else is somebody else's business you have been
 * given access to, and that is worth saying before the click, not after.
 */
function roleLabel(role?: VenueRole | null): string | null {
  if (!role || role === 'owner') return null;
  // Title case, not upper: the CSS applies the visual uppercase, and the DOM keeps
  // a readable word for screen readers.
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatVenueType(type?: string | null): string {
  if (!type) return 'venue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Venue Header (logo + name + optional dropdown for enterprise) ─────────────

interface VenueHeaderProps {
  venues: VenueWithRole[];
  currentVenue: Restaurant | null;
  isMultiVenue: boolean;
  canAddLocation: boolean;
  collapsed: boolean;
  onAddLocation: () => void;
}

function VenueHeader({ venues, currentVenue, isMultiVenue, canAddLocation, collapsed, onAddLocation }: VenueHeaderProps) {
  const [open, setOpen] = useState(false);

  // Owning one business and managing another is a supported state, so the list says
  // which is which. The heading only appears when there is something to separate —
  // a single-business owner sees the same plain list they always have.
  const owned = venues.filter(v => !roleLabel(v._role));
  const elsewhere = venues.filter(v => roleLabel(v._role));
  const venueGroups = elsewhere.length === 0
    ? [{ key: 'all', heading: null as string | null, venues }]
    : [
        { key: 'owned', heading: owned.length > 0 ? 'Your venues' : null, venues: owned },
        { key: 'elsewhere', heading: 'Managing for others', venues: elsewhere },
      ].filter(g => g.venues.length > 0);

  if (isMultiVenue || canAddLocation) {
    const extraLocations = venues.length - 1;
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(o => !o)}
          className={`w-full flex items-center gap-3 p-2.5 rounded-2xl transition-all duration-300 group ${open ? 'bg-black/5 dark:bg-white/5 ring-1 ring-black/10 dark:ring-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5'} ${collapsed ? 'justify-center' : ''}`}
        >
          <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center p-2 flex-shrink-0 shadow-inner">
            <img src="/logo-short-dark.svg" alt="GatheredHQ" className="w-full h-full object-contain dark:hidden" />
            <img src="/logo-short.svg" alt="GatheredHQ" className="w-full h-full object-contain hidden dark:block" />
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-black text-[15px] text-ink tracking-tight truncate leading-tight">
                  {currentVenue?.name ?? 'Select venue'}
                </p>
                <p className="text-ink-faint text-[10px] uppercase tracking-[1.5px] font-bold leading-none mt-1">
                  {extraLocations > 0 ? `+${extraLocations} more location${extraLocations > 1 ? 's' : ''}` : formatVenueType(currentVenue?.venue_type)}
                </p>
              </div>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${open ? 'bg-orange-500/10 text-orange-500' : 'bg-black/5 dark:bg-white/5 text-ink-faint group-hover:text-ink'}`}>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
              </div>
            </>
          )}
        </button>

        {open && !collapsed && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 right-0 top-full mt-2 bg-surface border border-border dark:bg-[#0d0d0d]/95 dark:border-white/[0.08] backdrop-blur-xl rounded-xl shadow-2xl z-50 overflow-hidden animate-scale-in origin-top">
              {/* "All venues" sits ABOVE the venue list, where the owner already goes to
                  change branch — that is the moment they are thinking about the group
                  rather than one venue. Only when there IS a group: for a single venue it
                  would be a worse copy of the dashboard they are already on. */}
              {venues.length > 1 && (
                <NavLink
                  to="/overview"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-[13px] border-b border-border dark:border-white/5 transition-all ${
                      isActive
                        ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold'
                        : 'hover:bg-black/5 dark:hover:bg-white/5 text-ink-muted hover:text-ink'
                    }`
                  }
                >
                  <div className="w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <LayoutGrid className="w-3.5 h-3.5 text-orange-500" />
                  </div>
                  <span className="truncate font-semibold">All venues</span>
                </NavLink>
              )}
              <div className="max-h-[300px] overflow-y-auto scrollbar-premium">
                {venueGroups.map(group => (
                  <Fragment key={group.key}>
                    {group.heading && (
                      <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.11em] text-ink-muted/70">
                        {group.heading}
                      </div>
                    )}
                    {group.venues.map(v => (
                  <NavLink
                    key={v.id}
                    to={`/venues/${v.slug}/overview`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-[13px] transition-all group ${
                        isActive
                          ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold'
                          : 'hover:bg-black/5 dark:hover:bg-white/5 text-ink-muted hover:text-ink'
                      }`
                    }
                  >
                    <div className="w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform overflow-hidden">
                      {v.logo_url ? (
                        <img src={v.logo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-orange-500 font-black text-[11px]">{v.name[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <span className="truncate font-medium flex-1 min-w-0">{v.name}</span>
                    {roleLabel(v._role) && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-[0.07em] bg-amber-500/15 text-amber-700 dark:text-amber-400">
                        {roleLabel(v._role)}
                      </span>
                    )}
                  </NavLink>
                    ))}
                  </Fragment>
                ))}
              </div>
              {canAddLocation && (
                <div className="border-t border-border dark:border-white/5 p-1 bg-surface-muted dark:bg-white/5">
                  <button
                    onClick={() => { setOpen(false); onAddLocation(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-bold text-orange-500 hover:bg-orange-500/10 rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    ADD NEW LOCATION
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 p-2.5 ${collapsed ? 'justify-center' : ''}`}>
      <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center p-2 flex-shrink-0 shadow-inner">
        <img src="/logo-short-dark.svg" alt="GatheredHQ" className="w-full h-full object-contain dark:hidden" />
        <img src="/logo-short.svg" alt="GatheredHQ" className="w-full h-full object-contain hidden dark:block" />
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <p className="font-black text-[15px] text-ink tracking-tight truncate leading-tight">{currentVenue?.name ?? 'My Venue'}</p>
          <p className="text-ink-faint text-[10px] uppercase tracking-[1.5px] font-bold leading-none mt-1">{formatVenueType(currentVenue?.venue_type)}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Sidebar ─────────────────────────────────────────────────────────────

export default function Sidebar() {
  const navigate = useNavigate();
  const { user, venues, isMultiVenue, refetchVenues } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (group: string) => setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  const [showAddLocation, setShowAddLocation] = useState(false);

  const { activeItems, isItemLocked, currentVenue, extraLocationCount } = useNavItems();

  // Show dropdown + "Add Location" when user has extra location slots or multiple venues
  const canAddLocation = isMultiVenue || venues.length > 1 || extraLocationCount > 0;

  return (
    <>
    <aside
      className={`hidden lg:flex flex-col sidebar-premium transition-all duration-500 ease-in-out ${collapsed ? 'w-22' : 'w-72'
        } h-screen sticky top-0 flex-shrink-0 z-40`}
    >
      {/* ── Logo / Venue Header ──────────────────────────────────────── */}
      <div className="p-2 flex-shrink-0">
        <VenueHeader
          venues={venues}
          currentVenue={currentVenue}
          isMultiVenue={isMultiVenue}
          canAddLocation={canAddLocation}
          collapsed={collapsed}
          onAddLocation={() => setShowAddLocation(true)}
        />
      </div>

      {/* ── Nav (scrollable) ─────────────────────────────────────────── */}
      <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto scrollbar-premium py-2">
        {(() => {
          // activeItems / lockedItems / isItemLocked are derived in useNavItems
          const renderItem = (item: NavItem, showDivider: boolean) => {
            /**
             * ⚠ Currently always false — the `showLock` branch below is UNREACHABLE.
             *
             * This only ever runs over `activeItems`, which useNavItems defines as
             * `visibleItems.filter(i => !isItemLocked(i))` — so nothing locked reaches
             * here. Topbar independently does the same via an early `return null`
             * ("hide tabs the current plan can't access"). Both surfaces HIDE what the
             * plan can't reach; the padlock/"Upgrade your plan to unlock" UI below is
             * left over from when they teased upgrades instead.
             *
             * Left in place because re-enabling teasers is a live product option — but
             * it is dead today. Don't read it as evidence the feature exists. Parity
             * between the two surfaces is enforced by
             * src/__tests__/components/layout/navParity.test.tsx.
             */
            const showLock = isItemLocked(item);
            return (
              <Fragment key={item.to}>
                {showDivider && (
                  <div className="mx-3 my-3 divider-sidebar" />
                )}
                {showLock ? (
                  <div
                    title={item.locked ? 'Coming soon' : 'Upgrade your plan to unlock'}
                    className={`flex items-center gap-4 px-4 py-3 rounded-2xl cursor-not-allowed select-none group transition-all duration-300 opacity-90 hover:opacity-40 ${collapsed ? 'justify-center' : 'justify-start'
                      } text-ink-faint`}
                  >
                    <div className="transition-transform group-hover:scale-105">
                      {item.icon}
                    </div>
                    {!collapsed && (
                      <>
                        <span className="text-[14px] font-medium flex-1 truncate">{item.label}</span>
                        <Lock className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" aria-label="locked" />
                      </>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 group ${collapsed ? 'justify-center' : 'justify-start'
                      } ${isActive
                        ? 'active-glow-vibrant text-ink dark:text-white shadow-sm dark:shadow-lg'
                        : 'text-ink-muted hover-glass-vibrant'
                      }`
                    }
                  >
                    <div className="relative overflow-visible transition-all duration-500 group-hover:scale-110 group-hover:text-ink dark:group-hover:text-white group-[.active-glow-vibrant]:text-orange-500 group-[.active-glow-vibrant]:drop-shadow-[0_0_8px_rgba(249,115,22,0.5)]">
                      {item.icon}
                      {collapsed && !!item.badge && item.badge > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-orange-500 rounded-full ring-2 ring-[var(--surface)] z-10" />
                      )}
                    </div>
                    {!collapsed && (
                      <>
                        <span className="text-[14px] font-bold flex-1 truncate tracking-tight">{item.label}</span>
                        {!!item.badge && item.badge > 0 && (
                          <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                )}
              </Fragment>
            );
          };

          return (
            <>
              {(() => {
                const rendered: React.ReactNode[] = [];
                const seenGroups = new Set<string>();

                for (const item of activeItems) {
                  if (item.group && !collapsed) {
                    if (!seenGroups.has(item.group)) {
                      seenGroups.add(item.group);
                      const groupItems = activeItems.filter(i => i.group === item.group);
                      const isOpen = openGroups[item.group] ?? false;
                      const hasActiveBadge = groupItems.some(i => (i.badge ?? 0) > 0);
                      const totalBadge = groupItems.reduce((sum, i) => sum + (i.badge ?? 0), 0);

                      rendered.push(
                        <Fragment key={`group-${item.group}`}>
                          {item.dividerBefore && <div className="mx-3 my-3 divider-sidebar" />}
                          <button
                            onClick={() => toggleGroup(item.group!)}
                            className="w-full flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 text-ink-muted hover-glass-vibrant"
                          >
                            <div className="transition-transform group-hover:scale-105">
                              {item.icon}
                            </div>
                            {!collapsed && (
                              <>
                                <span className="text-[14px] font-medium flex-1 text-left">{item.group}</span>
                                {!isOpen && hasActiveBadge && (
                                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{totalBadge}</span>
                                )}
                                <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                              </>
                            )}
                          </button>
                          {isOpen && (
                            <div className="ml-4">
                              {groupItems.map(gi => renderItem(gi, false))}
                            </div>
                          )}
                        </Fragment>,
                      );
                    }
                    // Skip — already rendered as part of group
                  } else {
                    rendered.push(renderItem(item, item.dividerBefore ?? false));
                  }
                }

                return rendered;
              })()}
            </>
          );
        })()}
      </nav>

      {/* ── Footer ─────────────────────────────────── */}
      <div className="flex-shrink-0">
        <div className="mx-3 divider-sidebar" />
        <div className="px-5 py-5">
          <button
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-full flex items-center justify-center p-3 rounded-2xl text-ink-muted hover:text-ink hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-all duration-300 group border border-transparent hover:border-border"
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5 group-hover:scale-125 transition-transform" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="ml-3 text-[13px] font-black uppercase tracking-[2px]">Minimize</span>
              </>
            )}
          </button>
        </div>
      </div>
    </aside>

    {showAddLocation && (
      <AddLocationModal
        plan={user?.plan}
        // The venues that can be copied FROM. Passed in rather than fetched: the sidebar
        // already has the list it renders the switcher from.
        existingVenues={venues}
        onClose={() => setShowAddLocation(false)}
        onSuccess={(venue) => {
          setShowAddLocation(false);
          void refetchVenues();
          navigate(`/venues/${venue.slug}/overview`);
        }}
      />
    )}
    </>
  );
}
